import { useTenantNavigate } from '@app-store'
import { openWithTenant } from '@app-store'
import {
  addToaster,
  DataTypeStore,
  hideLoader,
  LayoutResponseType,
  showLoader,
} from '@app-store'
import {
  TableActionType,
  Svg360View,
  MODE_CONSTANTS,
  ACTION_ICONS_NAMESPACE,
} from '@ui-kit'
import {
  NAMESPACES,
  LAYOUT_TYPE_NAMESPACE,
  ENTITY_NAME,
} from '../../utils/constants'
import { hasPermission, isReadOnlyUser } from '../../utils/permission'
import {
  entityRoute,
  viewRoute,
  tabRoute,
  editRoute,
  view360Route,
} from '../../routes/route.constant'
import { uniqueId } from 'lodash'
import { TOASTER_NAMESPACE } from '../../constants/addEditPage.strings'
import { useAppDispatch, useAppSelector } from '../../utils/agent.store'
import { ShortcutKeys, TAB_INDEX_DEFAULT } from '../../constants/form.constants'
import { openNewWindow } from '../../utils/helperFunctions'
import {
  getMigratedLayoutId,
  getMigrationStatus,
} from '../../utils/migration.utils'
export const useListingTableActions = (
  entityId: string | number,
  allLayouts: LayoutResponseType[] = [],
  actionsToShow: string[] = []
) => {
  const navigate = useTenantNavigate()
  const dispatch = useAppDispatch()

  const layoutExistsWithType = (type: string) => {
    return !!allLayouts.find(
      (layout) => layout.default_value && layout.type === type
    )
  }
  const entitiesByName = useAppSelector(
    (state) => state?.entity?.entitiesByName
  )

  const editLayoutListingAction = async (row: DataTypeStore) => {
    if (entityId?.toString() === ENTITY_NAME?.CASE_ENTITY_ID) {
      dispatch(showLoader())
      try {
        const migrated = await getMigrationStatus(Number(row?.id))
        if (migrated) {
          const layoutId = await getMigratedLayoutId()
          navigate(
            `/entities/32/tab/${row?.record_id ?? row?.id}?layoutId=${layoutId}`
          )
        } else {
          openNewWindow(
            `/entity/${entityId}/workflow/review/${row?.record_id ?? row?.id}`
          )
        }
      } catch (err) {
        console.error(err)
      } finally {
        dispatch(hideLoader())
      }
    } else {
      let rowId = row?.id
      let redirectEntity = entityId
      if (row?.entity_type) {
        rowId = row?.record_id
        const entityObject =
          entitiesByName?.[(row?.entity_type as string) ?? '']
        redirectEntity = entityObject?.id
      }

      navigate(`/${entityRoute}/${redirectEntity}/${editRoute}/${rowId}`)
    }
  }
  const handleLayoutNavigation = (
    row: DataTypeStore,
    redirectEntity: string
  ) => {
    const rowId = row?.record_id ?? row?.id
    if (layoutExistsWithType(LAYOUT_TYPE_NAMESPACE.OVERVIEW_TYPE)) {
      navigate(`/${entityRoute}/${redirectEntity}/${viewRoute}/${rowId}`)
    } else {
      navigate(`/${entityRoute}/${redirectEntity}/${tabRoute}/${rowId}`)
    }
  }
  const onActionClick = async (
    actionKey: string,
    row: DataTypeStore,
    openInNewTab?: boolean,
    preventRedirect?: boolean
  ) => {
    switch (actionKey) {
      case ACTION_ICONS_NAMESPACE.VIEW:
        if (entityId?.toString() === ENTITY_NAME?.CASE_ENTITY_ID) {
          dispatch(showLoader())
          try {
            const migrated = await getMigrationStatus(Number(row?.id))
            if (migrated) {
              const layoutId = await getMigratedLayoutId()
              navigate(
                `/entities/32/tab/${
                  row?.record_id ?? row?.id
                }?layoutId=${layoutId}`
              )
            } else {
              navigate(`/entities/32/view/${row?.record_id ?? row?.id}`)
            }
          } catch (err) {
            console.error(err)
          } finally {
            dispatch(hideLoader())
          }
        } else {
          let redirectEntity = entityId

          if (row?.entity_type) {
            const entityObject =
              entitiesByName?.[(row?.entity_type as string) ?? '']
            redirectEntity = entityObject?.id
          }
          if (preventRedirect) {
            return
          } else if (openInNewTab) {
            openWithTenant(
              `/${entityRoute}/${entityId}/${viewRoute}/${
                row?.record_id ?? row?.id
              }`,
              '_blank'
            )
          } else {
            handleLayoutNavigation(row, redirectEntity?.toString())
          }
        }
        break
      case ACTION_ICONS_NAMESPACE.EDIT:
        if (!isReadOnlyUser()) {
          editLayoutListingAction(row)
        }

        break
      case MODE_CONSTANTS.VIEW_360:
        if (layoutExistsWithType(LAYOUT_TYPE_NAMESPACE.VIEW_360_TYPE))
          navigate(
            `/${entityRoute}/${entityId}/${view360Route}/${
              row?.record_id ?? row?.id
            }`
          )
        else
          dispatch(
            addToaster({
              id: uniqueId(),
              message: TOASTER_NAMESPACE.LAYOUT_NOT_CREATED,
              type: 'error',
            })
          )

        break
    }
  }

  const actions: TableActionType<DataTypeStore>[] = []
  if (actionsToShow.includes(ACTION_ICONS_NAMESPACE.VIEW))
    actions.push({
      label: NAMESPACES.VIEW,
      key: ACTION_ICONS_NAMESPACE.VIEW,
      isEnabledOnDisabledRow: true,
    })

  if (actionsToShow.includes(NAMESPACES.VIEW_360_KEY))
    actions.push({
      label: NAMESPACES.VIEW_360,
      key: NAMESPACES.VIEW_360_KEY,
      isEnabledOnDisabledRow: false,
      type: 'component',
      renderActionComponent: (_row, _rowIndex, _onActionClick, isDisabled) => {
        if (isDisabled) {
          return <Svg360View pathClass="fill-accent-200" />
        }
        return (
          <button
            onClick={() =>
              _onActionClick(NAMESPACES.VIEW_360_KEY, _row, _rowIndex)
            }
            tabIndex={TAB_INDEX_DEFAULT}
            onKeyDown={(e) => {
              if (
                e?.key === ShortcutKeys.Enter ||
                e?.key === ShortcutKeys?.Space
              ) {
                e?.stopPropagation()
                e?.preventDefault()
                if (isDisabled) {
                  return
                } else {
                  _onActionClick(NAMESPACES.VIEW_360_KEY, _row, _rowIndex)
                }
              }
            }}
            className="svg-path-fill-secondary-hover bg-transparent border-0 p-0 cursor-pointer"
            type="button"
          >
            <Svg360View />
          </button>
        )
      },
    })

  if (!isReadOnlyUser()) {
    if (actionsToShow.includes(ACTION_ICONS_NAMESPACE.EDIT)) {
      if (hasPermission(MODE_CONSTANTS.EDIT, entityId)) {
        actions.push({
          label: NAMESPACES.EDIT,
          key: ACTION_ICONS_NAMESPACE.EDIT,
          isEnabledOnDisabledRow: false,
        })
      }
    }
  }

  return { actions, onActionClick }
}
